import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  CreditCard, Shield, Lock, CheckCircle, Zap, Clock, 
  Smartphone, Globe, Award, TrendingUp 
} from 'lucide-react';

const PaiementEnLigneSecurise = () => {
  const advantages = [
    {
      icon: <Shield className="w-12 h-12 text-gold" />,
      title: "100% Sécurisé",
      description: "Paiement crypté via Stripe, leader mondial de la sécurité des paiements en ligne"
    },
    {
      icon: <Zap className="w-12 h-12 text-gold" />,
      title: "Instantané",
      description: "Réservation confirmée immédiatement après le paiement, sans délai d'attente"
    },
    {
      icon: <Clock className="w-12 h-12 text-gold" />,
      title: "24h/24",
      description: "Payez à tout moment, jour et nuit, en quelques clics depuis chez vous"
    },
    {
      icon: <Smartphone className="w-12 h-12 text-gold" />,
      title: "Multi-Device",
      description: "Compatible mobile, tablette et ordinateur pour plus de flexibilité"
    },
    {
      icon: <CreditCard className="w-12 h-12 text-gold" />,
      title: "Tous Moyens de Paiement",
      description: "Visa, Mastercard, American Express acceptés en toute sécurité"
    },
    {
      icon: <Award className="w-12 h-12 text-gold" />,
      title: "Aucun Frais Caché",
      description: "Le prix affiché est le prix final, aucune surprise à l'arrivée"
    }
  ];

  const paymentSteps = [
    {
      number: "1",
      title: "Réservez Votre Course",
      description: "Remplissez le formulaire de réservation avec vos détails de trajet"
    },
    {
      number: "2",
      title: "Choisissez le Paiement en Ligne",
      description: "Sélectionnez l'option paiement sécurisé par carte bancaire"
    },
    {
      number: "3",
      title: "Payez en Toute Sécurité",
      description: "Entrez vos coordonnées bancaires sur notre plateforme cryptée Stripe"
    },
    {
      number: "4",
      title: "Confirmation Instantanée",
      description: "Recevez immédiatement votre confirmation de réservation par email"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Paiement en Ligne Sécurisé | Réservez Votre Taxi Bruxelles avec Carte Bancaire</title>
        <meta name="description" content="Payez votre taxi Bruxelles en ligne de manière 100% sécurisée. Paiement par carte bancaire instantané via Stripe. Réservation confirmée en temps réel. Disponible 24h/24." />
        <meta name="keywords" content="paiement en ligne taxi bruxelles, payer taxi carte bancaire, réservation taxi paiement sécurisé, stripe paiement taxi, payer en ligne chauffeur privé" />
        <link rel="canonical" href="https://www.luxurytransferservices.be/paiement-en-ligne-securise" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Paiement en Ligne Sécurisé - Taxi Bruxelles" />
        <meta property="og:description" content="Réservez et payez votre taxi Bruxelles en ligne en toute sécurité. Paiement instantané par carte bancaire." />
        <meta property="og:url" content="https://www.luxurytransferservices.be/paiement-en-ligne-securise" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "PaymentService",
            "name": "Paiement en Ligne Sécurisé - Luxury Transfer Service",
            "description": "Service de paiement en ligne sécurisé pour réserver votre taxi à Bruxelles",
            "url": "https://www.luxurytransferservices.be/paiement-en-ligne-securise",
            "provider": {
              "@type": "Organization",
              "name": "Luxury Transfer Service"
            },
            "paymentAccepted": ["Visa", "Mastercard", "American Express"],
            "availableChannel": {
              "@type": "ServiceChannel",
              "serviceUrl": "https://www.luxurytransferservices.be"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
        <Navbar />

        {/* Hero Section with CTA */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gold/10 to-transparent" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-gold to-amber-600 rounded-full mb-6">
                <Lock className="w-10 h-10 text-black" />
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Paiement en Ligne <span className="text-gold">100% Sécurisé</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
                Réservez votre taxi à Bruxelles et payez en toute sécurité par carte bancaire. 
                Confirmation instantanée garantie.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/">
                  <Button 
                    size="lg" 
                    className="bg-gold hover:bg-gold/90 text-black font-semibold px-8 py-6 text-lg"
                  >
                    <CreditCard className="mr-2 h-5 w-5" />
                    Réserver et Payer Maintenant
                  </Button>
                </Link>
                <Link to="/taxi-bruxelles">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-gold text-gold hover:bg-gold/10 px-8 py-6 text-lg"
                  >
                    Découvrir Nos Services
                  </Button>
                </Link>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center items-center gap-8 mt-12 pb-8 border-b border-gold/20">
              <div className="flex items-center gap-2 text-gray-300">
                <Shield className="w-6 h-6 text-gold" />
                <span className="font-semibold">Paiement Crypté SSL</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircle className="w-6 h-6 text-gold" />
                <span className="font-semibold">Stripe Certified</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Globe className="w-6 h-6 text-gold" />
                <span className="font-semibold">Conforme RGPD</span>
              </div>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
              Pourquoi Payer en Ligne ?
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {advantages.map((advantage, index) => (
                <Card key={index} className="bg-gray-800/50 border-gold/20 hover:border-gold/60 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="flex justify-center mb-4">
                      {advantage.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {advantage.title}
                    </h3>
                    <p className="text-gray-300">
                      {advantage.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-gray-900 to-black">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12">
              Comment Ça Marche ?
            </h2>

            <div className="space-y-6">
              {paymentSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-4 bg-gray-800/30 p-6 rounded-lg border border-gold/20">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-gold to-amber-600 rounded-full flex items-center justify-center text-black font-bold text-xl">
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-300">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Security Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="bg-gradient-to-br from-gold/10 to-amber-900/10 border border-gold/30 rounded-2xl p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Sécurité de Niveau Bancaire
                  </h2>
                  <p className="text-gray-300 mb-6">
                    Nous utilisons Stripe, la plateforme de paiement la plus sécurisée au monde, 
                    utilisée par des millions d'entreprises internationales.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-white">Cryptage SSL 256-bit</h4>
                        <p className="text-gray-400 text-sm">Vos données bancaires sont cryptées de bout en bout</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-white">3D Secure</h4>
                        <p className="text-gray-400 text-sm">Authentification renforcée pour votre sécurité</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-white">Aucune Donnée Stockée</h4>
                        <p className="text-gray-400 text-sm">Nous ne conservons jamais vos informations bancaires</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="w-64 h-64 bg-gradient-to-br from-gold/20 to-amber-600/20 rounded-full blur-3xl absolute inset-0" />
                    <div className="relative bg-gray-800 p-8 rounded-2xl border-2 border-gold shadow-2xl">
                      <div className="text-center">
                        <Shield className="w-32 h-32 text-gold mx-auto mb-4" />
                        <p className="text-white font-semibold text-lg">Paiement Sécurisé</p>
                        <p className="text-gray-400 text-sm">Powered by Stripe</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Prêt à Réserver Votre Course ?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Profitez du paiement en ligne sécurisé et de la confirmation instantanée
            </p>
            <Link to="/">
              <Button 
                size="lg" 
                className="bg-gold hover:bg-gold/90 text-black font-semibold px-12 py-6 text-xl"
              >
                <TrendingUp className="mr-2 h-6 w-6" />
                Réserver Maintenant
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PaiementEnLigneSecurise;
